import React, { createContext, useContext, useState, useEffect } from 'react';
import { getUserGeoLocation } from '../services/GeoLocationService';

// Language types
export type Language = 'en' | 'fr' | 'de';

// Translation value can be a string or an array of strings
export type TranslationValue = string | string[];

// Language context type
interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => TranslationValue;
  countryCode: string | null;
  loading: boolean;
  setManualCountryCode: (code: string) => void;
}

// Create context with default values
const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: (key: string) => key,
  countryCode: null,
  loading: true,
  setManualCountryCode: () => {}
});

// Custom hook to use the language context
export const useLanguage = () => useContext(LanguageContext);

// Language provider component
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Chargement initial
  const [loading, setLoading] = useState(true);
  
  // Stockage du code pays
  const [countryCode, setCountryCode] = useState<string | null>(null);
  
  // Get language from localStorage or use default
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language');
    return (savedLanguage as Language) || 'en';
  });
  
  // Translations state
  const [translations, setTranslations] = useState<Record<string, TranslationValue>>({});

  // Set language and save to localStorage
  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
    localStorage.setItem('language', newLanguage);
  };

  // Détecter le pays de l'utilisateur et définir la langue par défaut
  useEffect(() => {
    const detectUserCountry = async () => {
      try {
        console.log("Démarrage de la détection du pays...");
        
        // Vérifier si l'utilisateur a déjà sélectionné une langue manuellement
        const savedLanguage = localStorage.getItem('language');
        const hasUserSelectedLanguage = !!savedLanguage;
        
        console.log("Langue sauvegardée:", savedLanguage);
        console.log("L'utilisateur a sélectionné manuellement une langue:", hasUserSelectedLanguage);
        
        // Récupérer la géolocalisation dans tous les cas pour pouvoir afficher le pays dans l'interface
        console.log("Récupération des informations de géolocalisation...");
        const geoInfo = await getUserGeoLocation();
        
        // Stocker le code pays
        console.log("Pays détecté:", geoInfo.country, geoInfo.countryCode);
        setCountryCode(geoInfo.countryCode);
        
        // Si l'utilisateur n'a pas encore sélectionné de langue, on utilise la géolocalisation
        if (!hasUserSelectedLanguage) {
          // Définir la langue par défaut selon le pays
          if (geoInfo.language as Language) {
            console.log("Définition de la langue par défaut selon le pays:", geoInfo.language);
            setLanguage(geoInfo.language as Language);
          }
        }
        
        console.log(`Détecté pays: ${geoInfo.country} (${geoInfo.countryCode}), langue: ${geoInfo.language}`);
      } catch (error) {
        console.error('Erreur lors de la détection du pays:', error);
        
        // En cas d'erreur, définir un code pays par défaut
        console.log("Définition d'un code pays par défaut: FR");
        setCountryCode('FR');
      } finally {
        setLoading(false);
      }
    };

    detectUserCountry();
  }, []);

  // Load translations for current language
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const translationModule = await import(`../translations/${language}.json`);
        setTranslations(translationModule.default);
      } catch (error) {
        console.error(`Failed to load translations for ${language}`, error);
        // Fallback to English if translation file is not found
        if (language !== 'en') {
          const enModule = await import('../translations/en.json');
          setTranslations(enModule.default);
        }
      }
    };

    loadTranslations();
  }, [language]);

  // Translation function
  const t = (key: string): TranslationValue => {
    return translations[key] || key;
  };

  const value = {
    language,
    setLanguage,
    t,
    countryCode,
    loading,
    setManualCountryCode: (code: string) => {
      setCountryCode(code);
    }
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 